import { useCallback, useEffect, useState } from 'react';
import styles from './TimeClock.module.scss';
import classNames from 'classnames/bind';
import { Box, Button, InputLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CloseIcon, EyeIcon, PlusIcon, TrashIcon } from '~/assets/icons';
import { BaseDatePicker, BaseHeaderTitle, BaseRow, BaseSelect, LabelTimeHour } from '~/components/common';
import TimeClockList from '~/components/time-clocks/time-clock-list/TimeClockList';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { timeClocksActions } from '~/thunks/time-clocks/timeClocksSlice';
import { catchError, downloadCSV, handleApiError, notify } from '~/utils/helper';
import EditTimeClockDialog from '~/components/time-clocks/edit-time-clock-dialog/EditTimeClockDialog';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import usePermission from '~/hooks/usePermission';
import { handleGetTaskSelection } from '~/thunks/task/taskThunk';
import { handleGetTimeClock, handleGetTimeClocks, handleUpdateTimeClock } from '~/thunks/time-clocks/timeClocksThunk';
import { IDeleteTimeClock, ITimeClockEvent } from '~/utils/interface/timeClock';
import timeClockApi from '~/apis/timeclock';
import {
  EDIT_OPTION_BREAK_IN,
  EDIT_OPTION_BREAK_OUT,
  EDIT_OPTION_CHECK_OUT,
  TIME_CLOCK_STATUS_BREAK,
  TIME_CLOCK_STATUS_PROGRESS,
} from '~/utils/constants/time-clock';
import { PERMISSIONS } from '~/utils/constants/permission';
import dayjs from 'dayjs';
import { handleGetAccountsSelection } from '~/thunks/account/accountThunk';

import { startCase } from 'lodash';
const cx = classNames.bind(styles);

const TimeClock = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/time-clocks', 'admin/common']);
  const permissions = usePermission();
  //#endregion Declare Hook

  //#region Selector
  const { currentTimeClock, selectedIds, menuSelectedId, openAlertDelete, filter } = useAppSelector(
    (state) => state.timeClocks
  );
  const { selectionAccounts } = useAppSelector((state) => state.account);
  const [selectedAccount, setSelectedAccount] = useState<Array<string>>([]); // State for selected account
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const optionsWithAll = [{ id: 'all', name: 'All' }, ...selectionAccounts];
  //#endregion Selector

  //#region Declare State
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>(DIALOG_MODE_CREATE);
  const [editData, setEditData] = useState<ITimeClockEvent>();
  //#endregion Declare State

  //#region Implement Hook

  const handleExport = async () => {
    const now = dayjs().format('YYYY-MM-DD');
    const fileName = `Time_Clock_Export_${now}.csv`;

    if (selectedAccount.includes('all')) {
      const res = await timeClockApi.export({ startDate, endDate, isExport: true });
      downloadCSV(res.data, fileName);
    } else {
      const res = await timeClockApi.export({ startDate, endDate, createdBy: selectedAccount, isExport: true });
      downloadCSV(res.data, fileName);
    }
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(timeClocksActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(timeClocksActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedItems = useCallback(async () => {
    let payloadDeleteItems: IDeleteTimeClock =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await timeClockApi.deleteTimeClock(payloadDeleteItems);
      notify('success', t('deleteSuccessMessage', { ns: 'admin/common' }));
      dispatch(timeClocksActions.changeNeedRefreshData(true));
      dispatch(timeClocksActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  useEffect(() => {
    dispatch(handleGetTaskSelection());
  }, [dispatch]);

  useEffect(() => {
    dispatch(handleGetAccountsSelection({}))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    dispatch(handleGetTimeClocks(filter))
      .unwrap()
      .then((response) => {})
      .catch(handleApiError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleClickAddButton = () => {
    setEditDialogMode(DIALOG_MODE_CREATE);
    handleSetOpenDialog(true);
  };

  const handleSetOpenDialog = (isOpen: boolean = false, needRefreshData: boolean = false) => {
    setOpenEditDialog(isOpen);
    if (needRefreshData) dispatch(timeClocksActions.changeNeedRefreshData(true));
  };

  const handleClickViewTimeClock = () => {
    if (currentTimeClock?.id) {
      handleOpenEdit(currentTimeClock.id);
    }
  };

  const handleOpenEdit = async (id: string) => {
    try {
      const response = await dispatch(handleGetTimeClock(id)).unwrap();
      setEditData(response);
      setEditDialogMode(DIALOG_MODE_EDIT);
      setOpenEditDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleClickBreakTimeClock = () => {
    updateCurrentTimeClock(EDIT_OPTION_BREAK_IN);
  };

  const handleClickBreakOutTimeClock = () => {
    updateCurrentTimeClock(EDIT_OPTION_BREAK_OUT);
  };

  const handleClickCheckOutTimeClock = () => {
    updateCurrentTimeClock(EDIT_OPTION_CHECK_OUT);
  };

  const handleClickSwitchTimeClock = () => {
    updateCurrentTimeClock(EDIT_OPTION_CHECK_OUT);
    setEditDialogMode(DIALOG_MODE_CREATE);
    handleSetOpenDialog(true);
  };

  const updateCurrentTimeClock = async (option: string) => {
    try {
      await dispatch(
        handleUpdateTimeClock({
          id: currentTimeClock?.id ?? '',
          data: { taskId: currentTimeClock?.task?.id ?? '', jobId: currentTimeClock?.job?.id ?? '', option },
        })
      ).unwrap();
      notify('success', t('editSuccess', { ns: 'admin/common' }));
      dispatch(timeClocksActions.changeNeedRefreshData(true));
    } catch (e) {
      handleApiError(e);
    }
  };
  //#endregion Handle Function

  return (
    <Box className={cx('time-clock-page-container')}>
      <Box className={cx('header-title-container', 'flex-col min-[1365.99px]:flex-row min-[1365.99px]:items-center')}>
        <Box>
          {currentTimeClock && (
            <BaseRow className='gap-[40px] mb-[40px]'>
              <Box className='min-w-[200px]'>
                <Box className='text-[#797D8A] text-[16px]/[19.2px] mb-[10px]'>{t('currentTask')}</Box>
                <Box className='text-[#292D32] text-[24px]/[28.8px] tracking-wide'>
                  {startCase(currentTimeClock?.task?.name)}
                </Box>
              </Box>
              <Box className='min-w-[200px]'>
                <Box className='text-[#797D8A] text-[16px]/[19.2px] mb-[10px]'>{t('duration')}</Box>
                <Box className='text-[#292D32] text-[24px]/[28.8px] tracking-wide'>
                  {<LabelTimeHour zeroValue='-' time={currentTimeClock?.time} />}
                </Box>
              </Box>
            </BaseRow>
          )}
          <BaseHeaderTitle title={t('title')} />
        </Box>

        <Box className='flex flex-row mt-[10px] min-[1365.99px]:mt-0 self-start ml-auto'>
          <Button
            className={cx(
              'h-[36px] px-[14px] py-[9.5px] text-sm/[16.8px] font-medium rounded-[8px] mr-[10px] bg-[#D52B1E] hover:bg-[#d52b1ecc]',
              { 'visibility: visible': selectedIds.length > 0 },
              { 'visibility: hidden': selectedIds.length <= 0 }
            )}
            onClick={openAlertDeleteModal}
            variant='contained'
            disableElevation
            startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
          >
            {t('delete', { ns: 'admin/common' })}
          </Button>
          {permissions[PERMISSIONS.timeClock_create] &&
            (!currentTimeClock ? (
              <Button
                className='font-normal ml-[10px]'
                variant='contained'
                disableElevation
                startIcon={<PlusIcon className='w-[13px] h-[13px]' />}
                onClick={handleClickAddButton}
              >
                {t('addButton')}
              </Button>
            ) : (
              <>
                <Button
                  className='font-normal ml-[10px] text-[#86929D] border border-[#E5EAEF] hover:border-[#E5EAEF]'
                  variant='outlined'
                  startIcon={<EyeIcon className='w-[13px] h-[13px]' />}
                  onClick={handleClickViewTimeClock}
                >
                  {t('viewButton')}
                </Button>
                {currentTimeClock.status === TIME_CLOCK_STATUS_PROGRESS && (
                  <Button
                    className='font-normal ml-[10px] text-[#3F5D7C] bg-[#ECF2F8] hover:bg-[#ECF2F8]'
                    variant='contained'
                    startIcon={<CloseIcon className='w-[9px] h-[9px] [&>path]:fill-[#292D32]' />}
                    onClick={handleClickBreakTimeClock}
                  >
                    {t('breakButton')}
                  </Button>
                )}
                {currentTimeClock.status === TIME_CLOCK_STATUS_BREAK && (
                  <Button
                    className='font-normal ml-[10px] text-[#3F5D7C] bg-[#ECF2F8] hover:bg-[#ECF2F8]'
                    variant='contained'
                    startIcon={<CloseIcon className='w-[9px] h-[9px] [&>path]:fill-[#292D32]' />}
                    onClick={handleClickBreakOutTimeClock}
                  >
                    {t('breakOut')}
                  </Button>
                )}
                <Button className='font-normal ml-[10px]' variant='contained' onClick={handleClickCheckOutTimeClock}>
                  {t('checkOutButton')}
                </Button>
                <Button className='font-normal ml-[10px]' variant='contained' onClick={handleClickSwitchTimeClock}>
                  {t('switch')}
                </Button>
              </>
            ))}
        </Box>
      </Box>
      <BaseRow className='gap-[20px] '>
        <Box className='w-[100%]'>
          <InputLabel>{t('properties.startDate')}</InputLabel>
          <BaseDatePicker
            value={filter.startDate ?? startDate}
            onChange={(value) => {
              dispatch(
                timeClocksActions.setFilter({
                  startDate: dayjs(value).format('YYYY-MM-DD'),
                })
              );
              setStartDate(dayjs(value).format('YYYY-MM-DD'));
            }}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-[100%]'>
          <InputLabel>{t('properties.endDate')}</InputLabel>
          <BaseDatePicker
            value={filter.endDate ?? endDate}
            onChange={(value) => {
              dispatch(
                timeClocksActions.setFilter({
                  endDate: dayjs(value).format('YYYY-MM-DD'),
                })
              );
              setEndDate(dayjs(value).format('YYYY-MM-DD'));
            }}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-[100%]'>
          <InputLabel>{t('properties.createdBy')}</InputLabel>
          <BaseSelect
            options={optionsWithAll}
            value={selectedAccount}
            onChange={(event) => {
              const selectedValue = event.target.value;

              if (selectedValue.includes('all') && !selectedAccount.includes('all')) {
                dispatch(
                  timeClocksActions.setFilter({
                    createdBy: [],
                  })
                );
                setSelectedAccount(['all']);
              } else if (selectedValue !== 'all' && selectedAccount.includes('all')) {
                dispatch(
                  timeClocksActions.setFilter({
                    createdBy: selectedValue.filter((value: any) => value !== 'all'),
                  })
                );
                setSelectedAccount(selectedValue.filter((value: any) => value !== 'all'));
              } else {
                dispatch(
                  timeClocksActions.setFilter({
                    createdBy: selectedValue,
                  })
                );
                setSelectedAccount(selectedValue);
              }
            }}
            label={t('properties.createdBy')}
            placeholder={t('properties.createdBy')}
            multiple
          />
        </Box>

        {permissions[PERMISSIONS.timeClock_export] && (
          <Box className='w-[80%] mt-[12px]'>
            <InputLabel></InputLabel>
            <Button variant='contained' color='info' onClick={handleExport}>
              {t('export')}
            </Button>
          </Box>
        )}
      </BaseRow>

      <Box className={cx('content-container')}>
        <TimeClockList onOpenEdit={handleOpenEdit} />
      </Box>
      <EditTimeClockDialog open={openEditDialog} mode={editDialogMode} setOpen={handleSetOpenDialog} data={editData} />
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedItems}
      />
    </Box>
  );
};

export default TimeClock;
